import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Typography, 
  Button, 
  Menu, 
  MenuItem, 
  Drawer, 
  List, 
  ListItem, 
  ListItemText, 
  Box,
  Avatar,
  Chip,
  Fade,
  useScrollTrigger
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import WavesIcon from '@mui/icons-material/Waves';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import rareLogo from '../assets/rare_logo.png';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Overview', path: '/overview' },
  { label: 'Measurement & MEL', path: '/measurement-mel' },
  // Set hidden to false to show IMEL Plan Builder in the nav again.
  { label: 'IMEL Plan Builder', path: '/imel-plan-builder', hidden: true },
];

const visibleNavLinks = navLinks.filter((link) => !link.hidden);

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 10 });

  useEffect(() => {
    setIsScrolled(trigger);
  }, [trigger]);

  const toggleDrawer = (open) => () => setDrawerOpen(open);

  const isActivePath = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          backgroundColor: '#1A3E5F',
          borderBottom: '1px solid rgba(255, 255, 255, 0.32)',
          boxShadow: 'none',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 40,
        }}
      >
        <Toolbar 
          sx={{ 
            py: 1, 
            px: { xs: 2, sm: 3, md: 4 },
            maxWidth: 'xl',
            width: '100%',
            mx: 'auto',
          }}
        >
          {/* Logo Section */}
          <Box 
            component={RouterLink} 
            to="/" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              textDecoration: 'none',
              color: 'inherit',
              mr: 'auto',
            }}
          >
            <Box
              component="img"
              src={rareLogo}
              alt="Rare Logo"
              sx={{
                mr: 2,
                width: 40,
                height: 40,
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)',
              }}
            />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 700,
                  color: '#FFFFFF',
                  letterSpacing: '-0.015em',
                  fontSize: '1.125rem',
                }}
              >
                Fish Forever
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  display: 'block',
                  lineHeight: 1,
                  fontSize: '0.7rem',
                }}
              >
                Impact Framework
              </Typography>
            </Box>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.5 }}>
            {visibleNavLinks.map((link) => (
              (() => {
                const isImelLink = link.path === '/imel-plan-builder';
                const isActive = isActivePath(link.path);
                return (
              <Button
                key={link.label}
                component={RouterLink}
                to={link.path}
                disableRipple
                sx={{
                  color: isImelLink ? '#1A3E5F' : (isActive ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'),
                  backgroundColor: isImelLink ? '#E6A57E' : (isActive ? 'rgba(255, 255, 255, 0.15)' : 'transparent'),
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: isImelLink ? '#DEA07A' : (isActive ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.08)'),
                    color: isImelLink ? '#1A3E5F' : '#FFFFFF',
                  },
                }}
              >
                {link.label}
              </Button>
                );
              })()
            ))}
            
            {/* View Impact Data Button */}
            <Button
              component={RouterLink}
              to="/program-impact"
              variant="contained"
              size="small"
              endIcon={<ExpandMoreIcon sx={{ transform: 'rotate(-90deg)', fontSize: '1rem' }} />}
              sx={{
                ml: 1,
                py: 1,
                px: 2,
                fontSize: '0.875rem',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                color: '#1A3E5F',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              View Impact Data
            </Button>
          </Box>

          {/* Mobile Menu Button */}
          <Box sx={{ display: { xs: 'flex', lg: 'none' } }}>
            <IconButton
              onClick={toggleDrawer(true)}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Enhanced Mobile Drawer */}
          <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            sx={{
              '& .MuiDrawer-paper': {
                width: 280,
                backgroundColor: '#FFFFFF',
                border: 'none',
                borderLeft: '1px solid #DEE2E6',
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#212529" mb={2} fontSize="1rem">
                Navigation
              </Typography>
              <List sx={{ p: 0 }}>
                {visibleNavLinks.map((link) => (
                  (() => {
                    const isImelLink = link.path === '/imel-plan-builder';
                    const isActive = isActivePath(link.path);
                    return (
                  <ListItem
                    key={link.label}
                      button
                      component={RouterLink}
                      to={link.path}
                      onClick={toggleDrawer(false)}
                      sx={{
                      borderRadius: '8px',
                      mb: 0.5,
                      py: 1.5,
                      px: 2,
                      backgroundColor: isImelLink ? '#E6A57E' : (isActive ? '#F8F9FA' : 'transparent'),
                      border: isImelLink ? '1px solid #E6A57E' : (isActive ? '1px solid #DEE2E6' : '1px solid transparent'),
                        '&:hover': {
                        backgroundColor: isImelLink ? '#DEA07A' : '#F8F9FA',
                        }
                      }}
                    >
                      <ListItemText 
                        primary={link.label}
                        primaryTypographyProps={{
                        fontWeight: isActive ? 600 : 500,
                        color: isImelLink ? '#1A3E5F' : (isActive ? '#1A3E5F' : '#495057'),
                        fontSize: '0.875rem',
                        }}
                      />
                    </ListItem>
                    );
                  })()
                ))}
                <ListItem
                  button
                  component={RouterLink}
                  to="/program-impact"
                  onClick={toggleDrawer(false)}
                  sx={{
                    borderRadius: '8px',
                    mt: 2,
                    py: 1.5,
                    px: 2,
                    backgroundColor: '#1A3E5F',
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#2F6C75',
                    }
                  }}
                >
                  <ListItemText 
                    primary="View Impact Data"
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  />
                </ListItem>
              </List>
            </Box>
          </Drawer>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default Header; 